import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AvatarUpload } from "@/components/AvatarUpload";
import { AIAssistantAvatar } from "@/components/AIAssistantAvatar";
import { Separator } from "@/components/ui/separator";
import { ArrowLeft, Save } from "lucide-react";
import { SidebarProvider, SidebarInset } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";

interface AssistantProfile {
  id: string;
  name: string;
  company: string | null;
  avatar_url: string | null;
  description: string | null;
}

export default function AIAssistantProfile() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState<AssistantProfile | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    description: ""
  });

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const { data, error } = await supabase
        .from('ai_assistant_profile')
        .select('*')
        .eq('is_active', true)
        .single();

      if (error && error.code !== 'PGRST116') throw error;

      if (data) {
        setProfile(data);
        setFormData({
          name: data.name,
          company: data.company || "",
          description: data.description || ""
        });
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      toast({
        title: "Erro ao carregar perfil",
        description: "Não foi possível carregar o perfil do assistente",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast({
        title: "Nome obrigatório",
        description: "O nome do assistente é obrigatório",
        variant: "destructive"
      });
      return;
    }

    setSaving(true);
    try {
      if (profile) {
        // Update existing profile
        const { error } = await supabase
          .from('ai_assistant_profile')
          .update({
            name: formData.name.trim(),
            company: formData.company.trim() || null,
            description: formData.description.trim() || null
          })
          .eq('id', profile.id);

        if (error) throw error;
      } else {
        // Create new profile (deactivate others first)
        const { error: deactivateError } = await supabase
          .from('ai_assistant_profile')
          .update({ is_active: false })
          .neq('id', '00000000-0000-0000-0000-000000000000');

        if (deactivateError) throw deactivateError;

        const { error } = await supabase
          .from('ai_assistant_profile')
          .insert({
            name: formData.name.trim(),
            company: formData.company.trim() || null,
            description: formData.description.trim() || null,
            is_active: true
          });

        if (error) throw error;
      }

      toast({
        title: "Sucesso",
        description: "Perfil do assistente atualizado com sucesso"
      });

      await loadProfile();
    } catch (error) {
      console.error('Error saving profile:', error);
      toast({
        title: "Erro ao salvar",
        description: "Não foi possível salvar o perfil do assistente",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarUpload = async (url: string) => {
    if (!profile) return;

    try {
      const { error } = await supabase
        .from('ai_assistant_profile')
        .update({ avatar_url: url })
        .eq('id', profile.id);

      if (error) throw error;

      setProfile({ ...profile, avatar_url: url });
      toast({
        title: "Sucesso",
        description: "Avatar do assistente atualizado"
      });
    } catch (error) {
      console.error('Error updating avatar:', error);
      toast({
        title: "Erro",
        description: "Não foi possível atualizar o avatar",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">Carregando...</div>
      </div>
    );
  }

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <AppSidebar onClearData={() => {}} />
        <SidebarInset className="flex-1 overflow-auto">
          <div className="container max-w-4xl mx-auto py-8 px-4">
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className="mb-6"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Voltar
            </Button>

            <div className="space-y-6">
              <div>
                <h1 className="text-3xl font-bold">Perfil do Assistente de IA</h1>
                <p className="text-muted-foreground mt-2">
                  Configure a identidade e aparência do assistente virtual
                </p>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>Avatar</CardTitle>
                  <CardDescription>
                    Faça upload de uma foto para representar o assistente
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {profile && (
                    <AvatarUpload
                      currentAvatarUrl={profile.avatar_url}
                      userId={profile.id}
                      onUploadComplete={handleAvatarUpload}
                      userName={formData.name}
                      bucketName="ai-assistant-avatars"
                      folderPath="assistant"
                    />
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Informações Básicas</CardTitle>
                  <CardDescription>
                    Configure o nome e empresa do assistente
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Nome do Assistente *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Ex: Maria, Assistente RSVP"
                      maxLength={100}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="company">Empresa</Label>
                    <Input
                      id="company"
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      placeholder="Ex: RSVP System"
                      maxLength={100}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">Descrição / Biografia</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Descreva brevemente o assistente e suas capacidades..."
                      rows={4}
                      maxLength={500}
                    />
                    <p className="text-xs text-muted-foreground">
                      {formData.description.length}/500 caracteres
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Preview</CardTitle>
                  <CardDescription>
                    Veja como o assistente aparecerá no chat
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="border rounded-lg p-4 bg-muted/30">
                    <AIAssistantAvatar
                      name={formData.name || "Assistente de IA"}
                      company={formData.company || undefined}
                      avatarUrl={profile?.avatar_url}
                      size="lg"
                    />
                    {formData.description && (
                      <>
                        <Separator className="my-4" />
                        <p className="text-sm text-muted-foreground">
                          {formData.description}
                        </p>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>

              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => navigate(-1)}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={saving}
                >
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? "Salvando..." : "Salvar Alterações"}
                </Button>
              </div>
            </div>
          </div>
        </SidebarInset>
      </div>
    </SidebarProvider>
  );
}
